/** Observe installed updates without interrupting an open editor or another tab. */
export function watchWebUpdates(onReady: (registration: ServiceWorkerRegistration) => void) {
  if (!("serviceWorker" in navigator)) return () => {}
  const cleanup: Array<() => void> = []
  let disposed = false
  void navigator.serviceWorker
    .register("/sw.js", { updateViaCache: "none" })
    .catch(() => navigator.serviceWorker.getRegistration())
    .then((registration) => {
      if (!registration || disposed) return
      const ready = () => {
        if (registration.waiting && registration.active) onReady(registration)
      }
      const installing = () => {
        const worker = registration.installing
        if (!worker) return
        worker.addEventListener("statechange", ready)
        cleanup.push(() => worker.removeEventListener("statechange", ready))
      }
      let checking = false
      const check = () => {
        ready()
        if (checking || document.visibilityState !== "visible" || !navigator.onLine) return
        checking = true
        void registration
          .update()
          .catch(() => {})
          .finally(() => {
            checking = false
          })
      }
      registration.addEventListener("updatefound", installing)
      window.addEventListener("focus", check)
      window.addEventListener("online", check)
      document.addEventListener("visibilitychange", check)
      const timer = window.setInterval(check, 5 * 60_000)
      cleanup.push(() => {
        registration.removeEventListener("updatefound", installing)
        window.removeEventListener("focus", check)
        window.removeEventListener("online", check)
        document.removeEventListener("visibilitychange", check)
        window.clearInterval(timer)
      })
      installing()
      ready()
    })
    .catch(() => {
      // Offline or disabled service workers must not prevent opening the app.
    })
  return () => {
    disposed = true
    cleanup.forEach((dispose) => dispose())
  }
}

/** Call only after the user requests an update and pending drafts have been saved. */
export function activateWebUpdate(registration: ServiceWorkerRegistration): Promise<"updated" | "other-tabs"> {
  const worker = registration.waiting
  if (!worker) return Promise.reject(new Error("No installed update is available"))
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel()
    const finish = (result?: "updated" | "other-tabs") => {
      clearTimeout(timer)
      worker.removeEventListener("statechange", changed)
      channel.port1.close()
      if (result) resolve(result)
      else reject(new Error("The update could not be activated"))
    }
    const changed = () => {
      if (worker.state === "activated") finish("updated")
      if (worker.state === "redundant") finish()
    }
    const timer = window.setTimeout(() => finish(), 15_000)
    channel.port1.onmessage = (event) => finish(event.data === "other-tabs" ? "other-tabs" : undefined)
    worker.addEventListener("statechange", changed)
    worker.postMessage({ type: "OMM_ACTIVATE_UPDATE" }, [channel.port2])
  })
}
